import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  FlightTakeoff as TripIcon,
  LocationOn as LocationIcon,
  Schedule as DurationIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

const CreateTripFromGuideDialog = ({ open, onClose, guide }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(guide?.title || '');
  const [startDate, setStartDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // Calculate end date from start + duration
  const getEndDate = () => {
    if (!startDate || !guide?.duration_days) return '';
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + guide.duration_days - 1);
    return end.toISOString().split('T')[0];
  };

  const handleCreate = async () => {
    if (!startDate) {
      setError(t('guides.createTrip.startDateRequired', 'Please select a start date'));
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await api.post(`/guides/${guide.id}/create-trip`, {
        startDate,
        title: title.trim() || guide.title,
      });

      if (response.data.success) {
        setSuccess(response.data);
      }
    } catch (err) {
      console.error('Create trip error:', err);
      setError(err.response?.data?.error || t('guides.createTrip.error', 'Failed to create trip'));
    } finally {
      setCreating(false);
    }
  };

  const handleViewTrip = () => {
    if (success?.trip?.id) {
      navigate(`/trips/${success.trip.id}`);
    }
    onClose();
  };

  const handleClose = () => {
    setError('');
    setSuccess(null);
    onClose();
  };

  // Minimum date is today
  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TripIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('guides.createTrip.title', 'Create Trip from Guide')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('guides.createTrip.created', 'Trip Created!')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('guides.createTrip.createdDescription', 'Your trip has been created with the guide\'s itinerary. You can now customize it.')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {success.trip.itemCount} {t('guides.createTrip.itemsCopied', 'itinerary items copied')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleViewTrip}
              startIcon={<TripIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {t('guides.createTrip.viewTrip', 'View My Trip')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Guide Summary */}
            <Box sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                {guide?.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {guide?.destination && (
                  <Chip
                    icon={<LocationIcon sx={{ fontSize: 14 }} />}
                    label={guide.destination}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
                <Chip
                  icon={<DurationIcon sx={{ fontSize: 14 }} />}
                  label={`${guide?.duration_days || 1} ${(guide?.duration_days || 1) === 1 ? t('guides.day', 'day') : t('guides.days', 'days')}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary">
              {t('guides.createTrip.description', 'This will create a new trip with all the itinerary items from this guide. You can customize the trip after creation.')}
            </Typography>

            <TextField
              label={t('guides.createTrip.tripTitle', 'Trip Title')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              placeholder={guide?.title}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              label={t('guides.createTrip.startDate', 'When does your trip start?')}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {startDate && (
              <Box sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.50',
                border: '1px solid',
                borderColor: 'primary.100',
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {t('guides.createTrip.dateRange', 'Your trip will be from')}:{' '}
                  <strong>
                    {new Date(startDate).toLocaleDateString()} — {new Date(getEndDate()).toLocaleDateString()}
                  </strong>{' '}
                  ({guide?.duration_days || 1} {(guide?.duration_days || 1) === 1 ? t('guides.day', 'day') : t('guides.days', 'days')})
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} /> : <TripIcon />}
            onClick={handleCreate}
            disabled={creating || !startDate}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {creating
              ? t('guides.createTrip.creating', 'Creating...')
              : t('guides.createTrip.createButton', 'Create My Trip')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateTripFromGuideDialog;
